import type { QueuedRegistration } from '@/lib/offline/offlineDb'
import { outbox as defaultOutbox, type Outbox } from './outbox'
import { registrationService, RegistrationServiceError, type RegistrationService } from './registrationService'
import { ticketService, TicketServiceError, type TicketService } from '@/features/tickets/ticketService'
import { smsService, type SmsService } from '@/features/sms/smsService'

export type SyncResult = Readonly<{ sent: number; retrying: number; needsAttention: number; stoppedOffline: boolean }>

/** Failures a retry cannot fix: the server has understood and refused the registration. */
function isPermanent(error: unknown): error is RegistrationServiceError {
  return error instanceof RegistrationServiceError && (error.kind === 'invalid' || error.kind === 'session-missing' || error.kind === 'forbidden')
}

/** 5 s, 10 s, 20 s … capped at 5 minutes. */
export function backoffDelay(attempts: number): number {
  return Math.min(5 * 60_000, 5_000 * 2 ** Math.max(0, attempts - 1))
}

const message = (error: unknown) => (error instanceof Error ? error.message : 'The registration could not be sent.')

export interface SyncEngine {
  /** Sends everything due for this user. Concurrent calls share one run. */
  sync(userId: string): Promise<SyncResult>
}

/**
 * Sends queued registrations to the server, oldest first, one at a time.
 *
 * Each registration carries its client UUID, which the server treats as its
 * identity, so a send that times out after the server saved it is resent
 * safely: the server answers with the original. A record is removed only once
 * the server has accepted it.
 */
export function createSyncEngine({
  queue = defaultOutbox,
  service = registrationService,
  tickets = ticketService,
  sms = smsService,
  now = () => new Date(),
  isOnline = () => navigator.onLine,
}: {
  queue?: Outbox
  service?: Pick<RegistrationService, 'capture'> & Partial<Pick<RegistrationService, 'registerAtBoxOffice'>>
  tickets?: Pick<TicketService, 'issue'>
  sms?: Pick<SmsService, 'requestDrain'>
  now?: () => Date
  isOnline?: () => boolean
} = {}): SyncEngine {
  const running = new Map<string, Promise<SyncResult>>()

  async function send(record: QueuedRegistration) {
    await queue.markSyncing(record.clientRegistrationId, now())
    // A walk-up from the Box Office is registered and checked in together.
    const atBoxOffice = record.boxOffice && {
      clientRegistrationId: record.clientRegistrationId,
      eventId: record.eventId,
      formId: record.formId,
      contact: record.contact,
      answers: record.answers,
      capturedAt: record.capturedAt,
      clientCheckInId: record.boxOffice.clientCheckInId,
      origin: record.boxOffice.savedOffline ? 'offline' as const : 'online' as const,
    }
    const registrationId = atBoxOffice
      ? await (service.registerAtBoxOffice ? service.registerAtBoxOffice(atBoxOffice) : registrationService.registerAtBoxOffice(atBoxOffice))
      : await service.capture({
        clientRegistrationId: record.clientRegistrationId,
        sessionId: record.sessionId,
        formId: record.formId,
        contact: record.contact,
        answers: record.answers,
        capturedAt: record.capturedAt,
      })
    let issued = false
    try {
      if (record.ticketed) {
        await tickets.issue(registrationId)
        issued = true
      }
    } catch (error) {
      // A ticketing-mode mismatch is the normal successful result for an event
      // that does not use tickets. Every other failure remains queued: capture
      // retries return this same registration id, then issue retries safely.
      if (!(error instanceof TicketServiceError && error.code === 'ineligible')) throw error
    }
    // Sending a message is not part of registering someone. The drain is fired
    // and forgotten, so a delivery failure can neither hold up this run nor
    // leave a captured registration unsent: the attempt stays queued for a
    // later drain or an operator resend.
    if (issued) void sms.requestDrain().catch(() => undefined)
    await queue.markSynced(record.clientRegistrationId)
  }

  async function run(userId: string): Promise<SyncResult> {
    let sent = 0
    let retrying = 0
    let needsAttention = 0
    for (const record of await queue.due(userId, now())) {
      if (!isOnline()) return { sent, retrying, needsAttention, stoppedOffline: true }
      try {
        await send(record)
        sent += 1
      } catch (error) {
        // Removed by the person who registered them: the server will never take
        // it back, and keeping it would only send it again.
        if (error instanceof RegistrationServiceError && error.kind === 'removed') {
          await queue.markSynced(record.clientRegistrationId)
          continue
        }
        if (isPermanent(error)) {
          await queue.markNeedsAttention(record.clientRegistrationId, error.message)
          needsAttention += 1
          continue
        }
        const attempts = record.attempts + 1
        await queue.markRetry(record.clientRegistrationId, message(error), new Date(now().getTime() + backoffDelay(attempts)))
        retrying += 1
        // A connection failure will fail the rest too; try again on the next trigger.
        return { sent, retrying, needsAttention, stoppedOffline: !isOnline() || (error instanceof RegistrationServiceError && error.kind === 'offline') }
      }
    }
    return { sent, retrying, needsAttention, stoppedOffline: false }
  }

  return {
    sync(userId) {
      const existing = running.get(userId)
      if (existing) return existing
      const started = run(userId).finally(() => running.delete(userId))
      running.set(userId, started)
      return started
    },
  }
}
